/**
 * Logger Utility
 * Provides environment-aware logging with different log levels
 * In production, only errors are logged to console
 * In development, all logs are shown
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

/**
 * Log levels
 */
const LogLevel = {
  DEBUG: 'DEBUG',
  INFO: 'INFO',
  WARN: 'WARN',
  ERROR: 'ERROR'
};

/**
 * Logger class
 */
class Logger {
  constructor(context = 'App') {
    this.context = context;
  }

  /**
   * Create a logger instance with context
   * @param {string} context - Context name (e.g., 'ShopPage', 'CartContext')
   * @returns {Logger} Logger instance
   */
  static create(context) {
    return new Logger(context);
  }

  /**
   * Format log message with context and timestamp
   * @param {string} level - Log level
   * @param {any[]} args - Log arguments
   * @returns {string} Formatted message
   */
  formatMessage(level, args) {
    const timestamp = new Date().toISOString();
    const contextStr = `[${this.context}]`;
    const levelStr = `[${level}]`;
    return `${timestamp} ${levelStr} ${contextStr}`;
  }

  /**
   * Debug logs - only in development
   * @param {...any} args - Arguments to log
   */
  debug(...args) {
    if (isDevelopment) {
      console.log(this.formatMessage(LogLevel.DEBUG, args), ...args);
    }
  }

  /**
   * Info logs - only in development
   * @param {...any} args - Arguments to log
   */
  info(...args) {
    if (isDevelopment) {
      console.info(this.formatMessage(LogLevel.INFO, args), ...args);
    }
  }

  /**
   * Warning logs - shown in development, can be sent to error tracking in production
   * @param {...any} args - Arguments to log
   */
  warn(...args) {
    if (isDevelopment) {
      console.warn(this.formatMessage(LogLevel.WARN, args), ...args);
    } else if (isProduction) {
      // In production, warnings can be sent to error tracking service
      // Example: Sentry.captureMessage(args.join(' '), 'warning');
    }
  }

  /**
   * Error logs - always shown, should be sent to error tracking in production
   * @param {...any} args - Arguments to log
   */
  error(...args) {
    // Always log errors
    // Format error objects to show meaningful information
    const formattedArgs = args.map(arg => {
      if (arg instanceof Error) {
        return {
          message: arg.message,
          stack: arg.stack,
          name: arg.name,
          ...arg
        };
      } else if (typeof arg === 'object' && arg !== null) {
        // Try to stringify objects for better visibility
        try {
          return JSON.stringify(arg, null, 2);
        } catch (e) {
          return arg;
        }
      }
      return arg;
    });
    
    console.error(this.formatMessage(LogLevel.ERROR, args), ...formattedArgs);
    
    // In production, send to error tracking service
    if (isProduction) {
      // Example: Sentry.captureException(new Error(args.join(' ')));
    }
  }

  /**
   * Log API requests (useful for debugging)
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {object} data - Request data (optional)
   */
  api(method, url, data = null) {
    if (isDevelopment) {
      this.debug(`API ${method}`, url, data ? { data } : '');
    }
  }

  /**
   * Log API responses (useful for debugging)
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {object} response - Response data
   * @param {number} status - HTTP status code
   */
  apiResponse(method, url, response, status) {
    if (isDevelopment) {
      this.debug(`API ${method} Response [${status}]`, url, { response });
    }
  }

  /**
   * Log component lifecycle events (useful for debugging)
   * @param {string} event - Event name (e.g., 'mount', 'update', 'unmount')
   * @param {string} component - Component name
   */
  lifecycle(event, component) {
    if (isDevelopment) {
      this.debug(`Component ${event}`, component);
    }
  }
}

// Create default logger instance
const logger = new Logger('App');

// Export both default logger and Logger class
export default logger;
export { Logger, LogLevel };

